/**
 * Geolocation Detection Utility
 * Uses IP-based geolocation (ipapi.co free tier) to detect user's country
 * No user permissions required
 */

// Cache duration: 24 hours
const CACHE_DURATION = 24 * 60 * 60 * 1000;
const CACHE_KEY = 'user_country_cache';
const MANUAL_COUNTRY_KEY = 'user_country_manual';

/**
 * Get cached country data from localStorage
 * @returns {Object|null} Cached data or null if expired/missing
 */
function getCachedCountry() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { country, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid (< 24 hours old)
    if (now - timestamp < CACHE_DURATION) {
      console.log(`🌍 Using cached country: ${country}`);
      return country;
    }

    // Cache expired
    console.log('⏰ Country cache expired');
    localStorage.removeItem(CACHE_KEY);
    return null;
  } catch (error) {
    console.error('Error reading country cache:', error);
    return null;
  }
}

/**
 * Save country to localStorage cache
 * @param {string} country - Country code to cache
 */
function setCachedCountry(country) {
  try {
    const data = {
      country,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    console.log(`💾 Cached country: ${country}`);
  } catch (error) {
    console.error('Error caching country:', error);
  }
}

/**
 * Detect user's country using IP geolocation
 * @returns {Promise<string>} Country code (e.g., 'US', 'BR', 'ZA') or 'DEFAULT'
 */
export async function detectCountry() {
  // Check for manual country override first
  const manualCountry = getManualCountry();
  if (manualCountry) {
    console.log(`🎯 Using manually selected country: ${manualCountry}`);
    return manualCountry;
  }

  // Check cache first
  const cached = getCachedCountry();
  if (cached) {
    return cached;
  }

  // Call API to detect country
  console.log('🌍 Detecting country via IP geolocation...');

  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    // Check if API returned an error (rate limit, etc.)
    if (data.error) {
      console.warn('⚠️ Geolocation API error:', data.reason);
      return 'DEFAULT';
    }

    const countryCode = data.country_code || data.country || 'DEFAULT';

    console.log(`✅ Detected country: ${countryCode}`);
    console.log(`📍 Location: ${data.city || 'Unknown'}, ${data.region || 'Unknown'}`);

    // Cache the result
    setCachedCountry(countryCode);

    return countryCode;

  } catch (error) {
    console.error('❌ Failed to detect country:', error.message);
    console.log('🌐 Falling back to DEFAULT (worldwide)');

    // Cache the DEFAULT result to avoid repeated failed API calls
    setCachedCountry('DEFAULT');

    return 'DEFAULT';
  }
}

/**
 * Clear the country cache (useful for testing)
 */
export function clearCountryCache() {
  localStorage.removeItem(CACHE_KEY);
  console.log('🗑️ Country cache cleared');
}

/**
 * Force re-detection of country (clears cache and detects again)
 * @returns {Promise<string>} Country code
 */
export async function forceDetectCountry() {
  clearCountryCache();
  return detectCountry();
}

/**
 * Get manually selected country from localStorage
 * @returns {string|null} Country code or null if not set
 */
export function getManualCountry() {
  try {
    const manualCountry = localStorage.getItem(MANUAL_COUNTRY_KEY);
    return manualCountry;
  } catch (error) {
    console.error('Error reading manual country:', error);
    return null;
  }
}

/**
 * Set manually selected country in localStorage
 * @param {string} countryCode - Country code to save
 */
export function setManualCountry(countryCode) {
  try {
    localStorage.setItem(MANUAL_COUNTRY_KEY, countryCode);
    console.log(`💾 Saved manual country selection: ${countryCode}`);
  } catch (error) {
    console.error('Error saving manual country:', error);
  }
}

/**
 * Clear manual country selection
 */
export function clearManualCountry() {
  try {
    localStorage.removeItem(MANUAL_COUNTRY_KEY);
    console.log('🗑️ Cleared manual country selection');
  } catch (error) {
    console.error('Error clearing manual country:', error);
  }
}
